import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Snackbar, Alert } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import "../../styles/Announcement.css";
import AdminAnnouncementsCreate from "./AdminAnnouncementsCreate";
import AdminAnnouncementsEdit from "./AdminAnnouncementsEdit";
import AdminAnnouncementsView from "./AdminAnnouncementsView";
import { UserContext } from "../../App";
import { localDate } from "../Utils/DateHelpers";

const AdminAnnouncementsSearch = () => {
  const [pendingAnnouncements, setPendingAnnouncements] = useState([]);
  const [approvedAnnouncements, setApprovedAnnouncements] = useState([]);
  const [inactiveAnnouncements, setInactiveAnnouncements] = useState([]);
  const [updateTable, setUpdateTable] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [activeTab, setActiveTab] = useState("pending"); // "pending", "approved", or "inactive"
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { username, userType } = useContext(UserContext);
  const adminOwnerId = 1; // Replace with actual value if available

  const addLinksToMessage = (message) => {
    if(typeof message !== "string")
      return message;
    return message.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\{([^|]*?)\}/g, "<a style='color: maroon' target='_blank'>$1</a>").replace(/{(.*?)\|(.*?)}/g, "<a style='color: maroon' target='_blank'>$1</a>");
  };

  const fetchPendingAnnouncements = () => {
    axios
      .get("/admin/getPendingAnnouncements")
      .then((res) => {
        if (res.data.success) {
          setPendingAnnouncements(res.data.announcements.reverse());
        } else {
          setSnackbarMessage(
            res.data.message || "Error fetching pending announcements"
          );
          setSnackbarOpen(true);
        }
      })
      .catch(() => {
        setSnackbarMessage("Error fetching pending announcements");
        setSnackbarOpen(true);
      });
  };

  const fetchApprovedAnnouncements = () => {
    axios
      .get("/admin/getApprovedAnnouncements")
      .then((res) => {
        if (res.data.success) {
          setApprovedAnnouncements(res.data.announcements.reverse());
        } else {
          setSnackbarMessage(
            res.data.message || "Error fetching approved announcements"
          );
          setSnackbarOpen(true);
        }
      })
      .catch(() => {
        setSnackbarMessage("Error fetching approved announcements");
        setSnackbarOpen(true);
      });
  };

  const fetchInactiveAnnouncements = () => {
    axios
      .get("/admin/getInactiveAnnouncements")
      .then((res) => {
        if (res.data.success) {
          setInactiveAnnouncements(res.data.announcements.reverse());
        } else {
          setSnackbarMessage(
            res.data.message || "Error fetching inactive announcements"
          );
          setSnackbarOpen(true);
        }
      })
      .catch(() => {
        setSnackbarMessage("Error fetching inactive announcements");
        setSnackbarOpen(true);
      });
  };

  useEffect(() => {
    fetchPendingAnnouncements();
    fetchApprovedAnnouncements();
    fetchInactiveAnnouncements();
  }, [updateTable]);

  // Filter announcements by search query
  const filterAnnouncements = (announcements) => {
    if (!searchQuery.trim()) return announcements;
    const lower = searchQuery.toLowerCase();
    return announcements.filter(
      (ann) =>
        (ann.announcement && ann.announcement.toLowerCase().includes(lower)) ||
        (ann.description && ann.description.toLowerCase().includes(lower)) ||
        (ann.companyName && ann.companyName.toLowerCase().includes(lower)) ||
        (ann.adminName && ann.adminName.toLowerCase().includes(lower))
    );
  };

  const handleApprove = (announcementId) => {
    axios
      .post("/admin/approveAnnouncement", { announcementId })
      .then((res) => {
        if (res.data.success) {
          setSnackbarMessage("Announcement approved.");
          setUpdateTable(!updateTable);
        } else {
          setSnackbarMessage(res.data.message);
        }
        setSnackbarOpen(true);
      })
      .catch(() => {
        setSnackbarMessage("Error approving announcement");
        setSnackbarOpen(true);
      });
  };

  const handleDeny = (announcementId) => {
    axios
      .post("/admin/denyAnnouncement", { announcementId })
      .then((res) => {
        if (res.data.success) {
          setSnackbarMessage("Announcement denied.");
          setUpdateTable(!updateTable);
        } else {
          setSnackbarMessage(res.data.message);
        }
        setSnackbarOpen(true);
      })
      .catch(() => {
        setSnackbarMessage("Error denying announcement");
        setSnackbarOpen(true);
      });
  };

  // Update handleEdit to search in inactiveAnnouncements as well.
  const handleEdit = (announcementId) => {
    const announcement =
      pendingAnnouncements.find((a) => a.announcementId === announcementId) ||
      approvedAnnouncements.find((a) => a.announcementId === announcementId) ||
      inactiveAnnouncements.find((a) => a.announcementId === announcementId);
    if (announcement) {
      setSelectedAnnouncement(announcement);
      setOpenEdit(true);
    }
  };

  // New: Handle soft-delete (mark as "Deleted")
  const handleDelete = (announcementId) => {
    axios
      .post("/admin/deleteAnnouncement", { announcementId })
      .then((res) => {
        if (res.data.success) {
          setSnackbarMessage("Announcement marked as deleted.");
          setUpdateTable(!updateTable);
        } else {
          setSnackbarMessage(res.data.message);
        }
        setSnackbarOpen(true);
      })
      .catch(() => {
        setSnackbarMessage("Error deleting announcement");
        setSnackbarOpen(true);
      });
  };

  // New: Handle Reset/Repost for inactive announcements.
  const handleReset = (announcementId) => {
    axios
      .post("/admin/resetAnnouncement", { announcementId })
      .then((res) => {
        if (res.data.success) {
          setSnackbarMessage("Announcement reset to Approved.");
          setUpdateTable(!updateTable);
        } else {
          setSnackbarMessage(res.data.message);
        }
        setSnackbarOpen(true);
      })
      .catch(() => {
        setSnackbarMessage("Error resetting announcement");
        setSnackbarOpen(true);
      });
  };

  return (
    <div className="AdminAnnouncementsSearch bg-white p-6 rounded-xl w-full">
      {/* Top Row: Search Bar and Create Announcement Button */}
      <div className="flex items-center mb-6">
        <input
          type="text"
          placeholder="Search announcements..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-grow mr-4 px-4 py-2 border border-gray-300 rounded-lg"
        />
        <button
          type="button"
          onClick={() => setOpenCreate(true)}
          className="px-6 py-2 rounded-full border-2 border-gray-400 bg-white text-gray-800 font-semibold hover:bg-gray-100 hover:shadow-md transition">
          Create Announcement
        </button>
      </div>

      {/* Toggle Bar */}
      <div className="flex mb-6 border-b border-gray-300 shadow-sm">
        <button
          onClick={() => setActiveTab("pending")}
          className={`flex-1 py-2 text-center font-bold transition ${
            activeTab === "pending"
              ? "border-b-4 border-brand-primary text-brand-primary"
              : "text-gray-500"
          }`}>
          Pending
        </button>
        <button
          onClick={() => setActiveTab("approved")}
          className={`flex-1 py-2 text-center font-bold transition ${
            activeTab === "approved"
              ? "border-b-4 border-brand-primary text-brand-primary"
              : "text-gray-500"
          }`}>
          Approved
        </button>
        <button
          onClick={() => setActiveTab("inactive")}
          className={`flex-1 py-2 text-center font-bold transition ${
            activeTab === "inactive"
              ? "border-b-4 border-brand-primary text-brand-primary"
              : "text-gray-500"
          }`}>
          Inactive
        </button>
      </div>

      {/* Pending Announcements */}
      {activeTab === "pending" && (
        <>
          {filterAnnouncements(pendingAnnouncements).length === 0 ? (
            <div>No pending announcements.</div>
          ) : (
            filterAnnouncements(pendingAnnouncements).map((ann) => (
              <div
                key={ann.announcementId}
                className="mb-6 border border-gray-300 rounded-lg p-4 shadow-sm flex justify-between items-center cursor-pointer"
                onClick={() => {
                  setViewData(ann);
                  setOpenView(true);
                }}>
                <div className="pr-4">
                  <h3 className="text-xl font-semibold mb-1">
                    {ann.announcement}
                  </h3>
                  {ann.companyName && (
                    <p className="text-sm text-gray-600 mb-1">
                      From: {ann.companyName}
                    </p>
                  )}
                  <p
                    className="text-sm mb-1"
                    style={{
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      WebkitLineClamp: 2,
                    }}
                    dangerouslySetInnerHTML={{ __html: addLinksToMessage(ann.description) }}
                  >
                  </p>
                  <p className="text-xs text-gray-500">
                    Start:{" "}
                    {ann.startDate
                      ? localDate(ann.startDate).toLocaleDateString()
                      : "N/A"}{" "}
                    – Expires:{" "}
                    {ann.expireDate
                      ? localDate(ann.expireDate).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApprove(ann.announcementId);
                    }}
                    className="flex mr-2">
                    <CheckIcon
                      fontSize="large"
                      className="fill-brand-quaternary"
                    />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          "Are you sure you want to deny this announcement?"
                        )
                      ) {
                        handleDeny(ann.announcementId);
                      }
                    }}
                    className="flex">
                    <CloseIcon
                      fontSize="large"
                      className="fill-brand-primary"
                    />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(ann.announcementId);
                    }}
                    className="flex ml-2">
                    <EditIcon
                      fontSize="large"
                      className="fill-brand-quaternary"
                    />
                  </button>
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* Approved Announcements */}
      {activeTab === "approved" && (
        <>
          {filterAnnouncements(approvedAnnouncements).length === 0 ? (
            <div>No approved announcements.</div>
          ) : (
            filterAnnouncements(approvedAnnouncements).map((ann) => (
              <div
                key={ann.announcementId}
                className="mb-6 border border-gray-300 rounded-lg p-4 shadow-sm flex justify-between items-center cursor-pointer"
                onClick={() => {
                  setViewData(ann);
                  setOpenView(true);
                }}>
                <div>
                  <h3 className="text-xl font-semibold mb-1">
                    {ann.announcement}
                  </h3>
                  {ann.adminName ? (
                    <p className="text-sm text-gray-600 mb-1">
                      From: {ann.adminName}
                    </p>
                  ) : ann.companyName ? (
                    <p className="text-sm text-gray-600 mb-1">
                      From: {ann.companyName}
                    </p>
                  ) : null}
                  <p
                    className="text-sm mb-1"
                    style={{
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      WebkitLineClamp: 2,
                    }}
                    dangerouslySetInnerHTML={{ __html: addLinksToMessage(ann.description) }}
                  >
                  </p>
                  <p className="text-xs text-gray-500">
                    Start:{" "}
                    {ann.startDate
                      ? localDate(ann.startDate).toLocaleDateString()
                      : "N/A"}{" "}
                    – Expires:{" "}
                    {ann.expireDate
                      ? localDate(ann.expireDate).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(ann.announcementId);
                    }}
                    className="flex mr-4">
                    <EditIcon
                      fontSize="large"
                      className="fill-brand-quaternary"
                    />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          "Are you sure you want to delete this announcement?"
                        )
                      ) {
                        handleDelete(ann.announcementId);
                      }
                    }}
                    className="flex">
                    <CloseIcon
                      fontSize="large"
                      className="fill-brand-primary"
                    />
                  </button>
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* Inactive Announcements */}
      {activeTab === "inactive" && (
        <>
          {filterAnnouncements(inactiveAnnouncements).length === 0 ? (
            <div>No inactive announcements.</div>
          ) : (
            filterAnnouncements(inactiveAnnouncements).map((ann) => (
              <div
                key={ann.announcementId}
                className="mb-6 border border-gray-300 rounded-lg p-4 shadow-sm flex justify-between items-center cursor-pointer"
                onClick={() => {
                  setViewData(ann);
                  setOpenView(true);
                }}>
                <div>
                  <h3 className="text-xl font-semibold mb-1">
                    {ann.announcement}
                  </h3>
                  {ann.adminName ? (
                    <p className="text-sm text-gray-600 mb-1">
                      From: {ann.adminName}
                    </p>
                  ) : ann.companyName ? (
                    <p className="text-sm text-gray-600 mb-1">
                      From: {ann.companyName}
                    </p>
                  ) : null}
                  <p
                    className="text-sm mb-1"
                    style={{
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      WebkitLineClamp: 2,
                    }}
                    dangerouslySetInnerHTML={{ __html: addLinksToMessage(ann.description) }}
                  >
                  </p>
                  <p className="text-xs text-gray-500">
                    Start:{" "}
                    {ann.startDate
                      ? localDate(ann.startDate).toLocaleDateString()
                      : "N/A"}{" "}
                    – Expires:{" "}
                    {ann.expireDate
                      ? localDate(ann.expireDate).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(ann.announcementId);
                    }}
                    className="flex mr-4">
                    <EditIcon
                      fontSize="large"
                      className="fill-brand-quaternary"
                    />
                  </button>

                  {/* New Reset button for inactive announcements */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm("Reset this announcement to approved?")
                      ) {
                        handleReset(ann.announcementId);
                      }
                    }}
                    className="flex ml-2">
                    <span className="px-3 py-1 border border-gray-400 rounded-full text-sm text-gray-800 hover:bg-gray-100 transition">
                      Undo
                    </span>
                  </button>
                </div>
              </div>
            ))
          )}
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
          setSnackbarMessage("");
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert
          onClose={() => {
            setSnackbarOpen(false);
            setSnackbarMessage("");
          }}
          severity="info"
          sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Create Announcement Dialog */}
      <AdminAnnouncementsCreate
        open={openCreate}
        onClose={(refresh) => {
          setOpenCreate(false);
          if (refresh) setUpdateTable(!updateTable);
        }}
        adminOwnerId={adminOwnerId}
      />

      {/* Edit Announcement Dialog */}
      {selectedAnnouncement && (
        <AdminAnnouncementsEdit
          open={openEdit}
          onClose={(refresh) => {
            setOpenEdit(false);
            setSelectedAnnouncement(null);
            if (refresh) setUpdateTable(!updateTable);
          }}
          announcementData={selectedAnnouncement}
        />
      )}

      {/* View Announcement Dialog */}
      <AdminAnnouncementsView
        open={openView}
        onClose={() => setOpenView(false)}
        announcementData={viewData || {}}
      />
    </div>
  );
};

export default AdminAnnouncementsSearch;
